/** The collapsible anatomy tree. Props in, pixels out — it never fetches.
 *
 *  That boundary is the reuse story: this page builds a view tree from
 *  `/notifications/{id}/anatomy/`, and a paste-a-template mode builds one from
 *  a single `TemplateAnatomy`. Both render this.
 *
 *  Only the *visible* rows are rendered — `flattenVisible` walks expanded
 *  subtrees only — so a 329 KB monolith costs the same as a small one until
 *  someone opens it.
 */
import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';

import { flattenVisible } from './model/buildAnatomy';
import type { AnatomyViewNode } from './model/types';
import { AnatomyNodeRow } from './AnatomyNodeRow';

interface Props {
  root: AnatomyViewNode;
  expanded: ReadonlySet<string>;
  selectedId: string | null;
  /** Needed to build editor links — a line only means something for one record. */
  notificationId: number;
  onToggle(id: string): void;
  onSelect(id: string): void;
  onJumpToBlock?(alias: string): void;
  /** Whitespace-only text runs are folded away by default. */
  showBlank?: boolean;
  /** Tint rows whose content comes from a block rather than the layout. */
  highlightBlocks?: boolean;
}

export function AnatomyTree({
  root,
  expanded,
  selectedId,
  notificationId,
  onToggle,
  onSelect,
  onJumpToBlock,
  showBlank = false,
  highlightBlocks = false,
}: Props) {
  const rows = useMemo(
    () => flattenVisible(root, expanded, { showBlank }),
    [root, expanded, showBlank],
  );

  if (!rows.length) {
    return <Typography color="text.secondary">Nothing to show.</Typography>;
  }

  return (
    <Box role="tree" aria-label="Notification anatomy">
      {rows.map((row) => (
        <AnatomyNodeRow
          key={row.node.id}
          row={row}
          selected={row.node.id === selectedId}
          notificationId={notificationId}
          highlightBlocks={highlightBlocks}
          onToggle={onToggle}
          onSelect={onSelect}
          onJumpToBlock={onJumpToBlock}
        />
      ))}
    </Box>
  );
}

/** Rendered-row count, for the "showing N of M nodes" caption. */
export function visibleRowCount(
  root: AnatomyViewNode,
  expanded: ReadonlySet<string>,
  showBlank = false,
): number {
  return flattenVisible(root, expanded, { showBlank }).length;
}
